import { defineMessages } from 'react-intl';

/**
 * Content-design-owned thinking-indicator verbs, one finite set per personality
 * preset (see "Content Guidelines · How Rovo's personality shows up" → Thinking
 * traces). Copy is placeholder and structured so the final strings can be
 * swapped per preset without touching selection logic.
 *
 * Verbs are bare gerunds with no trailing ellipsis and no `{name}` — the
 * thinking indicator renders the agent icon and appends the animated ellipsis
 * itself. Keys are `<tonePrefix><n>`; `thinkingVerbSets` groups them into
 * per-tone arrays by key prefix (the prefixes are mutually non-overlapping).
 */
type MessageKeys =
	| 'rovoDefault1'
	| 'rovoDefault2'
	| 'rovoDefault3'
	| 'rovoDefault4'
	| 'rovoDefault5'
	| 'rovoDefault6'
	| 'rovoDefault7'
	| 'rovoDefault8'
	| 'rovoDefault9'
	| 'rovoDefault10'
	| 'professional1'
	| 'professional2'
	| 'professional3'
	| 'professional4'
	| 'professional5'
	| 'professional6'
	| 'professional7'
	| 'professional8'
	| 'professional9'
	| 'professional10'
	| 'professional11'
	| 'professional12'
	| 'empathetic1'
	| 'empathetic2'
	| 'empathetic3'
	| 'empathetic4'
	| 'empathetic5'
	| 'empathetic6'
	| 'empathetic7'
	| 'empathetic8'
	| 'creative1'
	| 'creative2'
	| 'creative3'
	| 'creative4'
	| 'creative5'
	| 'creative6'
	| 'creative7'
	| 'creative8'
	| 'creative9'
	| 'creative10'
	| 'creative11';

export const personalityThinkingVerbMessages: Record<
	MessageKeys,
	{ id: string; defaultMessage: string; description?: string }
> = defineMessages({
	rovoDefault1: {
		id: 'rovo-personalization.personality-thinking-verb.rovoDefault1',
		defaultMessage: 'Thinking',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the default (collaborative) personality preset; content-design owned.',
	},
	rovoDefault2: {
		id: 'rovo-personalization.personality-thinking-verb.rovoDefault2',
		defaultMessage: 'Connecting dots',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the default (collaborative) personality preset; content-design owned.',
	},
	rovoDefault3: {
		id: 'rovo-personalization.personality-thinking-verb.rovoDefault3',
		defaultMessage: 'Pulling threads',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the default (collaborative) personality preset; content-design owned.',
	},
	rovoDefault4: {
		id: 'rovo-personalization.personality-thinking-verb.rovoDefault4',
		defaultMessage: 'Dialing in',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the default (collaborative) personality preset; content-design owned.',
	},
	rovoDefault5: {
		id: 'rovo-personalization.personality-thinking-verb.rovoDefault5',
		defaultMessage: 'Spooling thoughts',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the default (collaborative) personality preset; content-design owned.',
	},
	rovoDefault6: {
		id: 'rovo-personalization.personality-thinking-verb.rovoDefault6',
		defaultMessage: 'Preparing',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the default (collaborative) personality preset; content-design owned.',
	},
	rovoDefault7: {
		id: 'rovo-personalization.personality-thinking-verb.rovoDefault7',
		defaultMessage: 'Humming',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the default (collaborative) personality preset; content-design owned.',
	},
	rovoDefault8: {
		id: 'rovo-personalization.personality-thinking-verb.rovoDefault8',
		defaultMessage: 'Crystallizing',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the default (collaborative) personality preset; content-design owned.',
	},
	rovoDefault9: {
		id: 'rovo-personalization.personality-thinking-verb.rovoDefault9',
		defaultMessage: 'Coalescing',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the default (collaborative) personality preset; content-design owned.',
	},
	rovoDefault10: {
		id: 'rovo-personalization.personality-thinking-verb.rovoDefault10',
		defaultMessage: 'Gathering context',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the default (collaborative) personality preset; content-design owned.',
	},
	professional1: {
		id: 'rovo-personalization.personality-thinking-verb.professional1',
		defaultMessage: 'Reviewing',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Professional personality preset; content-design owned.',
	},
	professional2: {
		id: 'rovo-personalization.personality-thinking-verb.professional2',
		defaultMessage: 'Checking sources',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Professional personality preset; content-design owned.',
	},
	professional3: {
		id: 'rovo-personalization.personality-thinking-verb.professional3',
		defaultMessage: 'Analyzing',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Professional personality preset; content-design owned.',
	},
	professional4: {
		id: 'rovo-personalization.personality-thinking-verb.professional4',
		defaultMessage: 'Organizing',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Professional personality preset; content-design owned.',
	},
	professional5: {
		id: 'rovo-personalization.personality-thinking-verb.professional5',
		defaultMessage: 'Evaluating',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Professional personality preset; content-design owned.',
	},
	professional6: {
		id: 'rovo-personalization.personality-thinking-verb.professional6',
		defaultMessage: 'Verifying',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Professional personality preset; content-design owned.',
	},
	professional7: {
		id: 'rovo-personalization.personality-thinking-verb.professional7',
		defaultMessage: 'Prioritizing',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Professional personality preset; content-design owned.',
	},
	professional8: {
		id: 'rovo-personalization.personality-thinking-verb.professional8',
		defaultMessage: 'Refining',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Professional personality preset; content-design owned.',
	},
	professional9: {
		id: 'rovo-personalization.personality-thinking-verb.professional9',
		defaultMessage: 'Assessing',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Professional personality preset; content-design owned.',
	},
	professional10: {
		id: 'rovo-personalization.personality-thinking-verb.professional10',
		defaultMessage: 'Validating',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Professional personality preset; content-design owned.',
	},
	professional11: {
		id: 'rovo-personalization.personality-thinking-verb.professional11',
		defaultMessage: 'Synthesizing',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Professional personality preset; content-design owned.',
	},
	professional12: {
		id: 'rovo-personalization.personality-thinking-verb.professional12',
		defaultMessage: 'Preparing',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Professional personality preset; content-design owned.',
	},
	empathetic1: {
		id: 'rovo-personalization.personality-thinking-verb.empathetic1',
		defaultMessage: 'Considering',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Empathetic personality preset; content-design owned.',
	},
	empathetic2: {
		id: 'rovo-personalization.personality-thinking-verb.empathetic2',
		defaultMessage: 'Clarifying',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Empathetic personality preset; content-design owned.',
	},
	empathetic3: {
		id: 'rovo-personalization.personality-thinking-verb.empathetic3',
		defaultMessage: 'Getting clarity',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Empathetic personality preset; content-design owned.',
	},
	empathetic4: {
		id: 'rovo-personalization.personality-thinking-verb.empathetic4',
		defaultMessage: 'Reframing',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Empathetic personality preset; content-design owned.',
	},
	empathetic5: {
		id: 'rovo-personalization.personality-thinking-verb.empathetic5',
		defaultMessage: 'Balancing',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Empathetic personality preset; content-design owned.',
	},
	empathetic6: {
		id: 'rovo-personalization.personality-thinking-verb.empathetic6',
		defaultMessage: 'Guiding',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Empathetic personality preset; content-design owned.',
	},
	empathetic7: {
		id: 'rovo-personalization.personality-thinking-verb.empathetic7',
		defaultMessage: 'Grounding',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Empathetic personality preset; content-design owned.',
	},
	empathetic8: {
		id: 'rovo-personalization.personality-thinking-verb.empathetic8',
		defaultMessage: 'Untangling',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Empathetic personality preset; content-design owned.',
	},
	creative1: {
		id: 'rovo-personalization.personality-thinking-verb.creative1',
		defaultMessage: 'Discombobulating',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Creative personality preset; content-design owned.',
	},
	creative2: {
		id: 'rovo-personalization.personality-thinking-verb.creative2',
		defaultMessage: 'Shimmering',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Creative personality preset; content-design owned.',
	},
	creative3: {
		id: 'rovo-personalization.personality-thinking-verb.creative3',
		defaultMessage: 'Spindling',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Creative personality preset; content-design owned.',
	},
	creative4: {
		id: 'rovo-personalization.personality-thinking-verb.creative4',
		defaultMessage: 'Thinking brilliantly',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Creative personality preset; content-design owned.',
	},
	creative5: {
		id: 'rovo-personalization.personality-thinking-verb.creative5',
		defaultMessage: 'Nudging neurons',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Creative personality preset; content-design owned.',
	},
	creative6: {
		id: 'rovo-personalization.personality-thinking-verb.creative6',
		defaultMessage: 'Sparking ideas',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Creative personality preset; content-design owned.',
	},
	creative7: {
		id: 'rovo-personalization.personality-thinking-verb.creative7',
		defaultMessage: 'Percolating',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Creative personality preset; content-design owned.',
	},
	creative8: {
		id: 'rovo-personalization.personality-thinking-verb.creative8',
		defaultMessage: 'Ruminating',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Creative personality preset; content-design owned.',
	},
	creative9: {
		id: 'rovo-personalization.personality-thinking-verb.creative9',
		defaultMessage: 'Weaving solutions',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Creative personality preset; content-design owned.',
	},
	creative10: {
		id: 'rovo-personalization.personality-thinking-verb.creative10',
		defaultMessage: 'Juggling options',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Creative personality preset; content-design owned.',
	},
	creative11: {
		id: 'rovo-personalization.personality-thinking-verb.creative11',
		defaultMessage: 'Peeking ahead',
		description:
			'Placeholder verb shown in the Rovo Chat thinking indicator for the Creative personality preset; content-design owned.',
	},
});
